import { api } from "./api";

// Paiement Stripe sans quitter l'application.
//
// Sur le web, on redirige simplement vers Stripe. Dans l'app, cette redirection
// ouvrirait Safari ou Chrome et ferait sortir l'utilisateur de l'app. On ouvre
// donc la page de paiement dans le navigateur intégré (le même que le calendrier
// de rendez-vous), puis on interroge le serveur : dès que le paiement est
// confirmé, on referme cette fenêtre et on affiche la confirmation dans l'app.

export function isNativeApp() {
  return (
    typeof window !== "undefined" &&
    !!window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform()
  );
}

function getBrowserPlugin() {
  return window.Capacitor?.Plugins?.Browser || null;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000; // large : le paiement peut inclure une validation bancaire

/**
 * Ouvre la page de paiement Stripe et attend son issue.
 *
 * @returns {Promise<"paid"|"cancelled"|"expired">}
 *  - "paid" : paiement confirmé (la fenêtre a été refermée)
 *  - "cancelled" : l'utilisateur a fermé la fenêtre sans payer
 *  - "expired" : la session Stripe a expiré
 */
export async function payWithStripeInApp(url, sessionId) {
  const Browser = getBrowserPlugin();
  if (!Browser?.open) {
    // Pas de navigateur intégré disponible : comportement web habituel.
    window.location.href = url;
    return "pending";
  }

  let closedByUser = false;
  let removeListener = null;
  try {
    const registration = Browser.addListener?.("browserFinished", () => {
      closedByUser = true;
    });
    const handle = await Promise.resolve(registration).catch(() => null);
    removeListener = handle?.remove ? () => handle.remove() : null;
  } catch (_) {
    /* l'écoute est un confort : sans elle, on s'appuie sur le sondage seul */
  }

  await Browser.open({ url, presentationStyle: "popover" });

  const startedAt = Date.now();
  try {
    while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

      let data = null;
      try {
        const res = await api.get(`/payments/stripe/status/${sessionId}`);
        data = res.data;
      } catch (_) {
        // Réseau instable pendant le paiement : on retentera au tour suivant.
      }

      if (data?.payment_status === "paid") {
        try {
          await Browser.close();
        } catch (_) {
          /* déjà refermée par l'utilisateur */
        }
        return "paid";
      }
      if (data?.status === "expired") {
        try {
          await Browser.close();
        } catch (_) {
          /* ignore */
        }
        return "expired";
      }

      // L'utilisateur a fermé la fenêtre : on laisse un tour de sondage
      // supplémentaire (le paiement peut avoir abouti juste avant la fermeture),
      // puis on considère l'achat abandonné.
      if (closedByUser) {
        try {
          const res = await api.get(`/payments/stripe/status/${sessionId}`);
          if (res.data?.payment_status === "paid") return "paid";
        } catch (_) {
          /* ignore */
        }
        return "cancelled";
      }
    }
    return "cancelled";
  } finally {
    if (removeListener) removeListener();
  }
}
